import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { SharedService } from '../../services/shared.service';
import { IdTypes, ModalID } from '../../models/constants';
import { BsDatepickerModule, BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { DateFormatDirective } from '../../directives/date-format.directive';
import {
  getStateNameByStateCode,
  getStateNamesWithPriority,
  toUpperCaseObject,
} from '../../functions';
import {
  minimumAgeValidator,
  notFutureDateValidator,
  notPastDateValidator,
} from '../../validators/custom-validators';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { DriverLicense } from '../../models/drivers-license-parser';

@Component({
  selector: 'app-enter-id',
  standalone: true,
  templateUrl: './enter-id.component.html',
  styleUrl: './enter-id.component.scss',
  imports: [ReactiveFormsModule, NgSelectModule, TooltipModule, BsDatepickerModule, DateFormatDirective],
})
export class EnterIdComponent {
  private destroyRef = inject(DestroyRef);
  subscriptions = new Subscription();
  parsed_drivers_license: DriverLicense = new DriverLicense();
  id_types = IdTypes;
  sexOptions = [
    { label: 'Male', value: 'M' },
    { label: 'Female', value: 'F' },
  ];
  // this.sharedService.drivers_license_scanned$.next
  issuing_authorities: string[];
  minimum_are_requirement: number;
  today: Date = new Date();
  scanIdModalOpen = false;
  id_form: FormGroup;
  bsConfig: Partial<BsDatepickerConfig>;
  constructor(
    public sharedService: SharedService,
    private fb: FormBuilder,
    public modalRef: BsModalRef,
  ) {
    // drivers_license_scanned$
    this.subscriptions.add(
      this.sharedService.drivers_license_scanned$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((parsed_license: DriverLicense) => {
          this.parsed_drivers_license = parsed_license;
          this.sharedService.recent_drivers_license.push(parsed_license);
          this.patchScannedDriversLicense();
          this.scanIdModalOpen = false;
        }),
    );
    this.bsConfig = Object.assign(
      {},
      {
        containerClass: 'theme-dark-blue',
        dateInputFormat: 'MM/DD/YYYY',
        showWeekNumbers: false,
        isAnimated: true,
        adaptivePosition: true,
      },
    );
  }
  ngOnInit() {
    // Get the value from initialState
    this.minimum_are_requirement =
      this.modalRef.content?.minimum_are_requirement ?? 18;
    this.issuing_authorities = getStateNamesWithPriority(
      this.sharedService.company_info.state,
    );

    this.id_form = this.fb.group({
      id_type: ['', Validators.required],
      issuing_authority: ['', Validators.required],
      name_first: ['', Validators.required],
      name_middle: ['', Validators.required],
      name_last: ['', Validators.required],
      birth_date: [
        '',
        [
          Validators.required,
          minimumAgeValidator(this.minimum_are_requirement),
        ],
      ],
      issue_date: ['', [Validators.required, notFutureDateValidator]],
      expiration_date: ['', [Validators.required, notPastDateValidator]],
      sex: ['', Validators.required],
      id_number: ['', Validators.required],
    });

    if (
      this.sharedService.dispo_build_manual_entered_id?.id_number?.length > 0
    ) {
      this.patchFormFromManualEnteredId();
    }
  }
  closeModalClick() {
    //console.warn(Object.keys(this.id_form.controls).filter(key => this.id_form.controls[key].invalid)) // finds invalid form fields
    this.sharedService.closeModal(ModalID.enter_id);
  }
  saveId() {
    const raw = this.id_form.getRawValue();
    const raw_to_uppercase = toUpperCaseObject(raw);

    this.sharedService.dispo_build_manual_entered_id = raw_to_uppercase;
    console.warn(this.sharedService.dispo_build_manual_entered_id);
    this.sharedService.manual_id_entered$.next(true);
    this.sharedService.closeModal(ModalID.enter_id);
  }
  patchFormFromManualEnteredId() {
    if (this.sharedService.dispo_build_manual_entered_id) {
      this.sharedService.dispo_build_manual_entered_id.id_type =
        this.sharedService.dispo_build_manual_entered_id.id_type.toLowerCase();
      this.id_form.patchValue(this.sharedService.dispo_build_manual_entered_id);
    }
  }
  patchScannedDriversLicense() {
    if (this.parsed_drivers_license) {
      this.id_form.patchValue({
        name_first: this.parsed_drivers_license.nameFirst || '',
        name_middle: this.parsed_drivers_license.nameMiddle || '',
        name_last: this.parsed_drivers_license.nameLast || '',
        birth_date: this.parsed_drivers_license.birthDate || '',
        sex: this.parsed_drivers_license.gender || '',
        id_number: this.parsed_drivers_license.licenseNumber || '',
        issuing_authority:
          getStateNameByStateCode(this.parsed_drivers_license.addressState) ||
          '',
        issue_date: this.parsed_drivers_license.issueDate || '',
        expiration_date: this.parsed_drivers_license.expirationDate || '',
      });
    }
  }
  openScanIdAndBlur(event: Event) {
    this.scanIdModalOpen = true;
    this.sharedService.openScanIdModal();
    (event.target as HTMLButtonElement).blur();
  }
  ngOnDestroy(): void {
  }
}
